import { useMemo, useCallback } from "react";
import { LinePath, Bar as VisxBar } from "@visx/shape";
import { curveMonotoneX } from "@visx/curve";
import { scaleTime, scaleLinear, scaleBand } from "@visx/scale";
import { AxisBottom, AxisLeft, AxisRight } from "@visx/axis";
import { GridRows } from "@visx/grid";
import { Group } from "@visx/group";
import { ParentSize } from "@visx/responsive";
import { useTooltip } from "@visx/tooltip";
import { localPoint } from "@visx/event";
import { bisector } from "d3-array";
import { ChartTooltip } from "../../../shared/components/ChartTooltip/ChartTooltip";
import {
  ChartLegend,
  type LegendItem,
} from "../../../shared/components/ChartLegend/ChartLegend";
import type { UserGrowthResponse } from "../../types";
import { formatNumber, formatShortDate } from "../../utils/formatters";
import styles from "./UserGrowthChart.module.css";

type DataPoint = UserGrowthResponse["data_points"][number];

const getDate = (d: DataPoint) => new Date(d.date + "T00:00:00Z");
const bisectDate = bisector<DataPoint, Date>((d) => getDate(d)).left;

const MARGIN = { top: 16, right: 56, bottom: 40, left: 48 };
const BAR_COLOR = "rgba(16, 185, 129, 0.6)";
const LINE_COLOR = "#6366f1";

const LEGEND_ITEMS: LegendItem[] = [
  { label: "New workspaces", color: BAR_COLOR },
  { label: "Cumulative", color: LINE_COLOR },
];

interface InnerChartProps {
  dataPoints: DataPoint[];
  width: number;
  height: number;
}

function InnerChart({ dataPoints, width, height }: InnerChartProps) {
  const {
    tooltipOpen,
    tooltipData,
    tooltipLeft,
    tooltipTop,
    showTooltip,
    hideTooltip,
  } = useTooltip<DataPoint>();

  const innerWidth = width - MARGIN.left - MARGIN.right;
  const innerHeight = height - MARGIN.top - MARGIN.bottom;

  const xScaleTime = useMemo(
    () =>
      scaleTime({
        domain: [
          getDate(dataPoints[0]),
          getDate(dataPoints[dataPoints.length - 1]),
        ],
        range: [0, innerWidth],
      }),
    [dataPoints, innerWidth],
  );

  const xScaleBand = useMemo(
    () =>
      scaleBand({
        domain: dataPoints.map((d) => d.date),
        range: [0, innerWidth],
        padding: 0.3,
      }),
    [dataPoints, innerWidth],
  );

  const yScaleLeft = useMemo(
    () =>
      scaleLinear({
        domain: [0, dataPoints.reduce((m, d) => Math.max(m, d.new_workspaces), 0) * 1.2 || 1],
        range: [innerHeight, 0],
        nice: true,
      }),
    [dataPoints, innerHeight],
  );

  const yScaleRight = useMemo(
    () =>
      scaleLinear({
        domain: [
          0,
          dataPoints.reduce((m, d) => Math.max(m, d.cumulative_workspaces), 0) * 1.1 || 1,
        ],
        range: [innerHeight, 0],
        nice: true,
      }),
    [dataPoints, innerHeight],
  );

  const handleTooltip = useCallback(
    (event: React.TouchEvent<SVGRectElement> | React.MouseEvent<SVGRectElement>) => {
      const point = localPoint(event);
      if (!point) return;

      const x0 = xScaleTime.invert(point.x - MARGIN.left);
      const idx = bisectDate(dataPoints, x0, 1);
      const d0 = dataPoints[Math.max(0, idx - 1)];
      const d1 = dataPoints[Math.min(idx, dataPoints.length - 1)];
      const d =
        d1 && Math.abs(x0.getTime() - getDate(d0).getTime()) >
               Math.abs(getDate(d1).getTime() - x0.getTime())
          ? d1
          : d0;

      showTooltip({
        tooltipData: d,
        tooltipLeft: xScaleTime(getDate(d)) + MARGIN.left,
        tooltipTop: yScaleRight(d.cumulative_workspaces) + MARGIN.top,
      });
    },
    [dataPoints, xScaleTime, yScaleRight, showTooltip],
  );

  if (innerWidth <= 0 || innerHeight <= 0) return null;

  return (
    <div className={styles.chartWrapper}>
      <svg width={width} height={height} role="img" aria-label="User growth over time">
        <Group left={MARGIN.left} top={MARGIN.top}>
          <GridRows
            scale={yScaleLeft}
            width={innerWidth}
            stroke="var(--colours-neutral-100, #f3f4f6)"
          />
          {/* Bars for new workspaces */}
          {dataPoints.map((d) => {
            const barWidth = xScaleBand.bandwidth();
            const barHeight = innerHeight - yScaleLeft(d.new_workspaces);
            const barX = xScaleBand(d.date) ?? 0;
            const barY = yScaleLeft(d.new_workspaces);
            return (
              <rect
                key={d.date}
                x={barX}
                y={barY}
                width={barWidth}
                height={Math.max(barHeight, 0)}
                fill={BAR_COLOR}
                rx={2}
              />
            );
          })}
          {/* Cumulative line */}
          <LinePath
            data={dataPoints}
            x={(d) => xScaleTime(getDate(d))}
            y={(d) => yScaleRight(d.cumulative_workspaces)}
            curve={curveMonotoneX}
            stroke={LINE_COLOR}
            strokeWidth={2}
          />
          <AxisBottom
            top={innerHeight}
            scale={xScaleTime}
            numTicks={Math.min(dataPoints.length, 6)}
            tickFormat={(d) =>
              formatShortDate((d as Date).toISOString().slice(0, 10))
            }
            stroke="var(--colours-neutral-200, #e5e7eb)"
            tickStroke="var(--colours-neutral-200, #e5e7eb)"
            tickLabelProps={() => ({
              fill: "var(--colours-neutral-500, #6b7280)",
              fontSize: 11,
              textAnchor: "middle" as const,
            })}
          />
          <AxisLeft
            scale={yScaleLeft}
            numTicks={5}
            label="New"
            labelOffset={32}
            stroke="var(--colours-neutral-200, #e5e7eb)"
            tickStroke="var(--colours-neutral-200, #e5e7eb)"
            tickLabelProps={() => ({
              fill: "var(--colours-neutral-500, #6b7280)",
              fontSize: 11,
              textAnchor: "end" as const,
              dx: -4,
              dy: 4,
            })}
            labelProps={{
              fill: BAR_COLOR,
              fontSize: 11,
              textAnchor: "middle" as const,
            }}
          />
          <AxisRight
            left={innerWidth}
            scale={yScaleRight}
            numTicks={5}
            label="Cumulative"
            labelOffset={36}
            stroke="var(--colours-neutral-200, #e5e7eb)"
            tickStroke="var(--colours-neutral-200, #e5e7eb)"
            tickLabelProps={() => ({
              fill: "var(--colours-neutral-500, #6b7280)",
              fontSize: 11,
              textAnchor: "start" as const,
              dx: 4,
              dy: 4,
            })}
            labelProps={{
              fill: LINE_COLOR,
              fontSize: 11,
              textAnchor: "middle" as const,
            }}
          />
          {/* Invisible overlay for tooltip */}
          <VisxBar
            width={innerWidth}
            height={innerHeight}
            fill="transparent"
            onTouchStart={handleTooltip}
            onTouchMove={handleTooltip}
            onMouseMove={handleTooltip}
            onMouseLeave={hideTooltip}
          />
          {/* Tooltip indicator */}
          {tooltipData && (
            <circle
              cx={xScaleTime(getDate(tooltipData))}
              cy={yScaleRight(tooltipData.cumulative_workspaces)}
              r={4}
              fill={LINE_COLOR}
              stroke="#fff"
              strokeWidth={2}
              pointerEvents="none"
            />
          )}
        </Group>
      </svg>
      {tooltipOpen && tooltipData && (
        <ChartTooltip top={tooltipTop ?? 0} left={tooltipLeft ?? 0}>
          <div className={styles.tooltipContent}>
            <span className={styles.tooltipDate}>
              {formatShortDate(tooltipData.date)}
            </span>
            <span>
              <strong>{formatNumber(tooltipData.new_workspaces)}</strong> new
            </span>
            <span>
              <strong>{formatNumber(tooltipData.cumulative_workspaces)}</strong>{" "}
              cumulative
            </span>
          </div>
        </ChartTooltip>
      )}
    </div>
  );
}

export interface UserGrowthChartProps {
  data: UserGrowthResponse;
}

const CHART_HEIGHT = 280;

export function UserGrowthChart({ data }: UserGrowthChartProps) {
  if (data.data_points.length === 0) {
    return (
      <div className={styles.emptyState}>No growth data for this period.</div>
    );
  }

  return (
    <div className={styles.container}>
      <ChartLegend items={LEGEND_ITEMS} />
      <ParentSize>
        {({ width }) => (
          <InnerChart
            dataPoints={data.data_points}
            width={width}
            height={CHART_HEIGHT}
          />
        )}
      </ParentSize>
    </div>
  );
}
